import { contactDisplayName, contactHandle, findContactByHints, type ZionContact } from '../storage/pairingStore.ts'
import { isAppleDevice } from '../ui/layout.ts'
import { isCapacitorNative } from '../api/ZionServer.ts'
import { navigate } from '../ui/router.ts'
import { t, tf } from '../i18n/index.ts'
import { PUSH_NOTIFY_ASSETS } from '../services/pushService.ts'
import { isNativePushPlatform } from '../push/nativePush.ts'
import { isActiveChatPeerVisible } from '../app/activeChatBridge.ts'
import {
  lookupContactNameForHints,
  lookupContactNameForPushData,
  notifyLabelFromContact,
} from '../storage/contactsDbForSw.ts'
import { collectPeerHintsFromPushData } from './notifyPeerHints.ts'
import { notifyDedupKey, shouldSkipDuplicateNotifyShared } from './notifyDedup.ts'

let currentChatContactIdGetter: (() => string) | null = null
let swInAppHooksInstalled = false

/** Evita import circolare zionApp ↔ messageNotify. */
export function setNotifyCurrentChatGetter(getter: () => string): void {
  currentChatContactIdGetter = getter
}

function viewingContactChat(contactId: string): boolean {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return false
  const cur = String(currentChatContactIdGetter?.() || '').trim()
  return Boolean(cur && cur === contactId)
}

function appVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

async function resolveNotifyTitle(peerId: string, pushData?: Record<string, unknown>): Promise<string> {
  if (pushData) {
    const fromPush = await lookupContactNameForPushData(pushData)
    if (fromPush) return fromPush
  }
  const peer = peerId.trim()
  if (peer) {
    const local = await lookupContactNameForHints([peer])
    if (local) return local
  }
  return PUSH_NOTIFY_ASSETS.defaultTitle
}

function isNativeApnsApp(): boolean {
  try {
    return isCapacitorNative() || isNativePushPlatform()
  } catch {
    return false
  }
}

export function playNotifyPop(): void {
  /* no-op */
}

/** iOS PWA: new Notification() spesso non mostra banner — usa SW showNotification. */
async function showLocalNotification(
  title: string,
  opts: NotificationOptions & { tag?: string },
  dedupKey: string,
  onClick?: () => void,
): Promise<void> {
  if (isNativeApnsApp()) return
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (await shouldSkipDuplicateNotifyShared(dedupKey)) return

  const safeTitle = String(title || '').trim() || PUSH_NOTIFY_ASSETS.defaultTitle
  const safeBody = String(opts.body || '').trim() || PUSH_NOTIFY_ASSETS.defaultBody
  const stableTag =
    opts.tag ||
    (dedupKey.startsWith('id:') ? `zion-msg-${dedupKey.slice(3)}` : `zion-${Date.now()}`)
  const options: NotificationOptions & { tag?: string; renotify?: boolean; silent?: boolean } = {
    ...opts,
    body: safeBody,
    icon: opts.icon || PUSH_NOTIFY_ASSETS.icon,
    badge: opts.badge || PUSH_NOTIFY_ASSETS.badge,
    tag: stableTag,
    renotify: false,
    silent: false,
  }
  ;(options as { sound?: string }).sound = 'default'
  delete (options as { vibrate?: number[] }).vibrate

  if (isAppleDevice() && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready
      await reg.showNotification(safeTitle, options)
      return
    } catch {
      /* fallback sotto */
    }
  }

  try {
    const n = new Notification(safeTitle, options)
    if (onClick) n.onclick = () => onClick()
  } catch {
    /* ignore */
  }
}

export async function notifyIncomingMessage(
  contact: ZionContact,
  preview: string,
  messageId?: string,
): Promise<void> {
  if (viewingContactChat(contact.contactId)) return
  if (isNativeApnsApp()) return

  const name = notifyLabelFromContact(contact) || contactDisplayName(contact)
  const msgId = String(messageId || '').trim()
  const body = preview.trim().slice(0, 140) || t('notify_default_body')
  const dedupKey = notifyDedupKey({ messageId: msgId })

  await showLocalNotification(
    name,
    {
      body,
      icon: PUSH_NOTIFY_ASSETS.icon,
      tag: msgId ? `zion-msg-${msgId}` : undefined,
    },
    dedupKey,
    () => {
      window.focus()
      navigate('chat', { contactId: contact.contactId })
    },
  )
}

export async function notifyTempIncomingMessage(
  contact: ZionContact,
  chatKey: string,
  preview: string,
  messageId?: string,
): Promise<void> {
  if (isNativeApnsApp()) return

  const handle = notifyLabelFromContact(contact) || contactHandle(contact)
  const body = preview.trim().slice(0, 140) || t('notify_temp_default_body')
  const msgId = String(messageId || '').trim()
  const dedupKey = notifyDedupKey({ messageId: msgId || `temp:${chatKey}` })

  await showLocalNotification(
    tf('notify_temp_title_prefix', { name: handle }),
    {
      body,
      icon: PUSH_NOTIFY_ASSETS.icon,
      tag: msgId ? `zion-msg-${msgId}` : `zion-temp-${chatKey}`,
    },
    dedupKey,
    () => {
      window.focus()
      navigate('tempChat', { chatKey })
    },
  )
}

function pushPreviewBody(data: Record<string, unknown>): string {
  let body = String(data.body || '').trim()
  if (!body || body.toLowerCase().includes('hai ricevuto')) {
    return PUSH_NOTIFY_ASSETS.defaultBody
  }
  const bodyFrom = body.match(/^(?:messaggio da|from)\s+(.+)$/i)
  if (bodyFrom) return PUSH_NOTIFY_ASSETS.defaultBody
  const colonIdx = body.indexOf(':')
  if (colonIdx > 0 && colonIdx < 64) {
    const prefix = body.slice(0, colonIdx).trim()
    if (/^[a-f0-9-]{8,}$/i.test(prefix)) {
      body = body.slice(colonIdx + 1).trim() || PUSH_NOTIFY_ASSETS.defaultBody
    }
  }
  return body.slice(0, 140) || PUSH_NOTIFY_ASSETS.defaultBody
}

/** Push SW → pagina: stesso banner del socket (nome da contatto locale). */
export async function handleSwPushIncoming(data: Record<string, unknown> | null | undefined): Promise<void> {
  if (!data || typeof data !== 'object') return
  if (isNativeApnsApp()) return
  if (appVisible()) return

  const hints = collectPeerHintsFromPushData(data)
  const contact = await findContactByHints(hints)
  if (!contact) return

  if (viewingContactChat(contact.contactId)) return
  const peerHint = hints[0] || contact.sessionToken
  if (peerHint && isActiveChatPeerVisible(peerHint)) return

  const msgId = String(data.messageId || data.message_id || data.id || '').trim()
  const body = pushPreviewBody(data)

  await notifyIncomingMessage(contact, body, msgId)
}

/** Push ricevuto dal SW (fallback legacy). */
export async function handleSwInAppNotify(data: Record<string, unknown> | null | undefined): Promise<void> {
  if (!data || data.type !== 'ZION_INAPP_NOTIFY') return
  if (isNativeApnsApp()) return
  if (appVisible()) return

  const peerId = String(data.peerId || '').trim()
  if (peerId && isActiveChatPeerVisible(peerId)) return

  const title = await resolveNotifyTitle(peerId, data)
  const body = String(data.body || '').trim() || PUSH_NOTIFY_ASSETS.defaultBody
  const msgId = String(data.messageId || '').trim()
  const dedupKey = notifyDedupKey({ messageId: msgId })

  void showLocalNotification(
    title,
    {
      body,
      icon: PUSH_NOTIFY_ASSETS.icon,
      tag: msgId ? `zion-msg-${msgId}` : undefined,
    },
    dedupKey,
  )
}

export function installSwInAppNotifyHooks(): void {
  if (swInAppHooksInstalled || typeof navigator === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  if (isNativeApnsApp()) return
  swInAppHooksInstalled = true
  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data as Record<string, unknown>
    if (msg?.type === 'ZION_PUSH_INCOMING') {
      void handleSwPushIncoming(msg.data as Record<string, unknown>)
      return
    }
    void handleSwInAppNotify(msg)
  })
}

export async function ensureDesktopNotificationPermission(): Promise<boolean> {
  try {
    if (isNativeApnsApp()) return false
  } catch {
    /* ignore */
  }
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const perm = await Notification.requestPermission()
  return perm === 'granted'
}

export { unlockNotificationAudio } from './audioUnlock.ts'
